"use client"

import { JSX, useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { HpcWorkflow } from "@/types/hpc-jobs"
import {
  formatJobTimestamp,
  formatRefreshedAgo,
  formatStatusCountsTooltip,
  getWorkflowStatusVariant,
  describeWorkflowStatus,
  canCancelWorkflow,
  formatCostUsd,
} from "@/lib/hpc-jobs-utils"
import { Badge } from "@/components/ui/badge"
import { CancelJobButton } from "@/components/hpc-jobs/CancelJobButton"
import { TruncatedCell } from "@/components/shared/TruncatedCell"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { CopyButton } from "@/components/ui/copy-button.tsx"

/**
 * How often the "updated X min ago" labels are re-rendered, in milliseconds.
 * The labels use minutes, so a little under a minute keeps them accurate.
 */
const REFRESHED_AGO_TICK_MS = 30_000

/** Tooltip on the cost column explaining where the number comes from. */
const ESTIMATED_COST_HINT =
  "Estimated from the hourly rates shown at submit and each task's run time. " +
  "Updated with the status; tasks without an estimate are left out."

interface HpcWorkflowsTableProps {
  /** The list of workflows to display. */
  workflows: HpcWorkflow[]
  /** The currently active tab, passed to the workflow detail page for back navigation. */
  currentTab: "running" | "completed"
  /** Whether this table is rendered in the admin view, used for back-navigation context. */
  isAdmin?: boolean
  /** Called after a workflow is successfully cancelled so the parent can refresh. */
  onJobCancelled?: () => void
}

/**
 * Returns the current time, re-rendering the caller every `intervalMs`.
 *
 * Used so relative labels ("updated 2 min ago") keep counting up between data
 * refreshes.
 *
 * @param intervalMs - How often to update, in milliseconds.
 * @returns The current time as a Date.
 */
function useNow(intervalMs: number): Date {
  const [now, setNow] = useState<Date>(() => new Date())

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), intervalMs)
    return () => clearInterval(interval)
  }, [intervalMs])

  return now
}

/**
 * Table component displaying HPC workflows, one row per workflow.
 *
 * Status and task counts come straight from the backend's workflow record. For
 * workflows that are still being refreshed, the status cell also shows how old
 * the counts are; finalised workflows show no age because they won't change.
 *
 * @param workflows - Array of HpcWorkflow objects to render as table rows.
 * @param currentTab - The active tab, threaded onto the detail link.
 * @param isAdmin - Whether the table is in the admin view.
 * @param onJobCancelled - Called after a workflow is cancelled.
 * @returns A table element displaying workflows, or an empty state message.
 */
export function HpcWorkflowsTable({
  workflows,
  currentTab,
  isAdmin,
  onJobCancelled,
}: HpcWorkflowsTableProps): JSX.Element {
  const [searchParams] = useSearchParams()
  const now = useNow(REFRESHED_AGO_TICK_MS)
  // Carry the selected look-back window onto the detail link so returning to
  // the overview keeps the same range, matching the tab/from threading.
  const rangeParam: string | null = searchParams.get("range")
  const rangeSuffix: string = rangeParam ? `&range=${rangeParam}` : ""

  if (workflows.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No workflows found matching the current filters.
      </div>
    )
  }

  return (
    <div className="rounded-md border overflow-hidden">
      <Table className="table-fixed w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[17%]">Workflow ID</TableHead>
            <TableHead className="w-[17%]">Workflow Name</TableHead>
            <TableHead className="w-[10%]">User</TableHead>
            <TableHead className="w-[12%]">Status</TableHead>
            <TableHead className="w-[12%]">Submitted</TableHead>
            <TableHead className="w-[12%]">Completed</TableHead>
            <TableHead className="w-[7%]">Tasks</TableHead>
            <TableHead className="w-[8%] text-right" title={ESTIMATED_COST_HINT}>
              Est. cost
            </TableHead>
            <TableHead className="w-[5%]">
              <span className="sr-only">Cancel</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {workflows.map((workflow) => {
            // Per-status task breakdown, e.g. "SUCCEEDED: 2, RUNNING: 1".
            const countsTooltip = formatStatusCountsTooltip(workflow.taskStateCounts)
            // Explain the less obvious statuses, otherwise show the breakdown.
            const statusTooltip = describeWorkflowStatus(workflow.status) ?? countsTooltip
            // Finalised workflows have no next refresh: their counts are final.
            const isStillRefreshing = workflow.nextRefreshAt !== undefined

            return (
              <TableRow key={workflow.workflowId}>
                <TableCell className="overflow-hidden">
                  <div className="flex items-center gap-1 min-w-0">
                    <CopyButton text={workflow.workflowId} />
                    <Link
                      to={`/computing/hpc/workflow/${encodeURIComponent(workflow.workflowId)}?tab=${currentTab}${isAdmin ? "&from=admin" : ""}${rangeSuffix}`}
                      className="font-mono text-xs truncate hover:underline text-primary"
                      title={workflow.workflowId}
                    >
                      {workflow.workflowId}
                    </Link>
                  </div>
                </TableCell>
                <TableCell>
                  <TruncatedCell value={workflow.workflowName} />
                </TableCell>
                <TableCell>
                  <TruncatedCell value={workflow.user} />
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-0.5">
                    <Badge
                      variant={getWorkflowStatusVariant(workflow.status)}
                      title={statusTooltip}
                    >
                      {workflow.status}
                    </Badge>
                    {isStillRefreshing && (
                      <span className="text-xs text-muted-foreground">
                        {formatRefreshedAgo(workflow.countsRefreshedAt, now)}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm whitespace-nowrap">
                  {formatJobTimestamp(workflow.submittedAt)}
                </TableCell>
                <TableCell className="text-sm whitespace-nowrap">
                  {formatJobTimestamp(workflow.completedAt)}
                </TableCell>
                <TableCell
                  className="text-sm whitespace-nowrap"
                  title={countsTooltip}
                  aria-label={`${workflow.completedTaskCount} of ${workflow.taskCount} tasks finished`}
                >
                  {workflow.completedTaskCount}/{workflow.taskCount}
                </TableCell>
                <TableCell className="text-sm text-right whitespace-nowrap">
                  {/* Cost so far for running workflows; "-" when there's no estimate. */}
                  {formatCostUsd(workflow.estimatedCostUsd)}
                </TableCell>
                <TableCell>
                  {/* Cancelling a workflow cancels every job in it; only shown while tasks can still run. */}
                  {canCancelWorkflow(workflow.status) && onJobCancelled && (
                    <CancelJobButton
                      cancelId={workflow.workflowId}
                      name={workflow.workflowName ?? workflow.workflowId}
                      entityType="workflow"
                      onCancelled={onJobCancelled}
                    />
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
